import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
// @ts-ignore
import styles from './Modal.module.scss';
import { useAppContext } from '../../Services/AppContext';
import { joinClasses } from '../../Utils/className.util';
import Text from '../Base/Text/Text';
import { Size } from '../../Enums/Size.enum';
import ModalWrapper from './ModalWrapper';

interface ModalProps {
	opened: boolean;
	fullScreen?: boolean;
	onClose?: () => void;
	size?: Size;
	maxHeight?: boolean;
	contentScroll?: boolean;
	wrapChat?: boolean;
	contentStyle?: object;
	forceClose?: boolean;
	style?: React.CSSProperties;
}

export interface ComponentModalProps {
	opened: boolean;
	onClose: any;
}

interface ModalHeaderProps {
	title: string;
	onClose?: () => void;
	firstUpper?: boolean;
	closeButton?: JSX.Element;
	hideClose?: boolean;
}

const Header = ({
	title,
	onClose,
	firstUpper,
	closeButton,
	hideClose,
}: ModalHeaderProps) => {
	const { translation } = useAppContext();
	const handleClose = e => {
		e.stopPropagation(); // dont propagate click to parent
		e.preventDefault();

		onClose?.();
	};

	useEffect(() => {
		if (hideClose) {
			return;
		}
		const close = e => {
			if (e.keyCode === 27) {
				handleClose();
			}
		};
		window.addEventListener('keydown', close);
		return () => window.removeEventListener('keydown', close);
	}, [hideClose]);

	const closeButtonElement = useMemo(() => {
		if (hideClose) {
			return null;
		}
		if (closeButton) {
			return React.cloneElement(closeButton, { onClick: handleClose });
		} else if (onClose) {
			return (
				<div className={styles.close} onClick={handleClose}>
					{translation['base.shared.close']}
				</div>
			);
		} else {
			return null;
		}
	}, [closeButton, handleClose, onClose, translation, hideClose]);

	return (
		<div className={styles.header}>
			<div className={styles.headerTitle}>
				<Text tx={title} inline={true} firstUpper={firstUpper} />
			</div>
			{closeButtonElement}
		</div>
	);
};

const Content = props => {
	return (
		<div
			style={props.contentStyle}
			className={joinClasses([
				styles.content,
				props.fullScreen && styles.fullScreenScroll,
				props.maxHeight && styles.maxHeight,
				props.contentScroll && styles.contentScroll,
			])}>
			{props.children}
		</div>
	);
};

const Footer = props => {
	return <div className={styles.footer}>{props.children}</div>;
};

class Modal extends React.Component {
	private el: HTMLDivElement;
	private modalRoot: HTMLElement;
	static Header: (props) => JSX.Element;
	static Content: (props) => JSX.Element;
	static Footer: (props) => JSX.Element;
	public props: any;

	constructor(props: ModalProps) {
		super(props);
		this.modalRoot = document.getElementById('modal-root');
		this.el = document.createElement('div');
	}

	componentDidMount() {
		const modalRoot = this.modalRoot;
		if (modalRoot) {
			modalRoot.appendChild(this.el);
		}
	}

	componentWillUnmount() {
		const modalRoot = this.modalRoot;
		if (modalRoot) {
			modalRoot.removeChild(this.el);
		}
	}

	handleClose(props) {
		props.onClose?.();
	}

	render() {
		return ReactDOM.createPortal(
			<>
				<ModalWrapper
					isOpen={this.props.opened}
					wrapChat={this.props.wrapChat}
					fullScreen={this.props.fullScreen}
					size={this.props.size}
					onClose={() => this.handleClose(this.props)}
					children={this.props.children}
					forceClose={this.props.forceClose}
					style={this.props.style}
				/>
			</>,
			this.el,
		);
	}
}

Modal.Header = Header;
Modal.Content = Content;
Modal.Footer = Footer;

export default Modal;
